import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { User } from '../../../../user/user';
import { LoadingSpinnerService } from '../../../../services/loading-spinner.service';
import { AuthService } from '../../../../services/auth.service';
import { ToasterService } from 'angular2-toaster';
import { FormGroup, Validators, FormControl } from '@angular/forms';
import { AnalyticsService } from '../../../../services/analytics.service';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import * as US_STATES from '../../../../../assets/countries/us';
import * as CANADA_STATES from '../../../../../assets/countries/canada';
import { log } from 'util';

function passwordMatchValidator(g: FormGroup) {
  return g.get('password').value === g.get('passwordConfirm').value ? null : { mismatch: true };
}

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {
  @Output() onUserUpdate = new EventEmitter();
  @Output() onUserRegister = new EventEmitter();
  email: string;
  password: string;
  user: any = new User();
  newUser: FormGroup;
  states: any = US_STATES.default.states;
  country: any = 'US';
  companyName: string;
  showCompanyField = false;
  stateProvince: any = US_STATES.default.states[0];
  showValidationErrors = false;

  constructor(
    private loadingSpinnerService: LoadingSpinnerService,
    private auth: AuthService,
    private toasterService: ToasterService,
    private analyticsService: AnalyticsService
  ) {}

  createForm() {
    this.newUser = new FormGroup(
      {
        firstName: new FormControl('', [Validators.required]),
        lastName: new FormControl('', [Validators.required]),
        email: new FormControl('', [Validators.required, Validators.email]),
        // phone: new FormControl('', ),
        phone: new FormControl('', [this.optionalPhoneValidator]),
        // phone: new FormControl('', [Validators.required, this.phoneValidator]),
        // phone: new FormControl('', [Validators.required, Validators.pattern(/^\d{3}\d{3}\d{4}$/)]),
        companyName: new FormControl(''),
        password: new FormControl('', [Validators.required]),
        passwordConfirm: new FormControl('', [Validators.required]),
      },
    passwordMatchValidator
    );
  }

  optionalPhoneValidator(control: FormControl): { [key: string]: boolean } | null {
    const value = (control.value || '').toString().trim();
    if (value === '') {
      return null;
    }
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.length >= 7 ? null : { invalidPhone: true };
  }


  phoneValidator(control: FormControl): { [key: string]: boolean } | null {
    let value = control.value ? control.value.trim() : '';

    // Add formatting
    if (/^\d{10}$/.test(value)) {
      value = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
      control.setValue(value, { emitEvent: false }); // Updating value in control
    }
    if (!/^\d{3}-\d{3}-\d{4}$/.test(value)) {
      return { invalidPhone: true };
    }
    return null;
  }

  private _showToast(type: string, title: string, body: string) {
    this.toasterService.popAsync(type, title, body);
  }

  private _setUser() {
    const user = this.auth.getUser();
    if (user) {
      this.user = user;
    } else {
      this.user.userType = 'Homeowner';
      this.user.country = this.country;
      this.user.companyName = this.companyName;
      this.user.stateProvince = this.stateProvince;
    }
    this.createForm();
    this.showCompanyFieldFunction(this.user.userType);
  }

  ngOnInit() {
    this._setUser();
  }

  onSetUserType(type: string) {
    this.user.userType = type;
    this.showCompanyFieldFunction(type);
  }

  onSetUserCountry(country: string) {
    this.user.country = country;
  }

  onSetUserStateProvince(state: string) {
    this.user.stateProvince = state;
  }

  onRegister(): void {
    this._syncFormFromUser();
    this.newUser.updateValueAndValidity();
    this.newUser.markAsTouched();
    Object.keys(this.newUser.controls).forEach(key => {
      this.newUser.get(key).markAsTouched();
    });

    const validationMessage = this._getValidationMessage();
    if (validationMessage) {
      this.showValidationErrors = true;
      this._showToast('error', 'Validation error', validationMessage);
      this._scrollToFirstInvalidField();
      return;
    }

    this.showValidationErrors = false;

    if (this.user.phone) {
      this.user.phone = this.user.phone.replace(/\D/g, '');
    }
    this.loadingSpinnerService.spinnerLoadingListAdd('register');
    if (this.user.id) {
      this.auth
        .updateUser(this.user)
        .pipe(
          switchMap(user => {
            this.auth.setUser(user);
            this.loadingSpinnerService.spinnerLoadingListRemove('register');
            this._showToast('success', 'Success', 'Profile updated');
            this.onUserUpdate.emit();
            this.analyticsService.onUserLoggedIn(this.auth.userId);
            return of(true);
          })
        )
        .subscribe();
    } else {
      // console.log('else this.user', this.user);
      this.auth
        .register(this.user)
        .pipe(
          switchMap(user => {
            this._showToast('success', 'Success', 'Registration completed. Now You can login.');
            this.loadingSpinnerService.spinnerLoadingListRemove('register');
            // console.log(user);
            // console.log('Sign up: isAuthorization = true');

            this.auth.setUser(user);
            this.onUserRegister.emit();
            return of(true);
          }),
          catchError(err => {
            const message =
              err &&
              err.error &&
              err.error.error &&
              err.error.error.message
                ? err.error.error.message
                : 'Registration failed. Please try again.';
            if (message.includes('Email already exists')) {
              this._showToast(
                'error',
                'Registration error',
                'User with this email address already exists'
              );
            } else {
              this._showToast('error', 'Save error', message);
            }
            this.loadingSpinnerService.spinnerLoadingListRemove('register');
            return ErrorObservable.create('Email already exists');
          })
        )
        .subscribe();
    }
  }

  chooseCountryStates() {
    this.onSetUserCountry(this.country);
    switch (this.country) {
      case 'US': this.states = US_STATES.default.states;
        break;
      case 'Canada': this.states = CANADA_STATES.default.states;
        break;
    }
    this.onSetUserStateProvince(this.states[0]);
  }

  showCompanyFieldFunction(type) {
    if (type === 'Homeowner') {
      this.showCompanyField = false;
    } else {
      this.showCompanyField = true;
    }
  }

  isFieldInvalid(controlName: string): boolean {
    if (!this.showValidationErrors || !this.newUser) {
      return false;
    }

    if (controlName === 'companyName' && this.showCompanyField && !this.user.companyName) {
      return true;
    }

    const control = this.newUser.get(controlName);
    if (!control) {
      return false;
    }

    if (control.invalid) {
      return true;
    }

    if (
      (controlName === 'password' || controlName === 'passwordConfirm') &&
      this._passwordsMismatch()
    ) {
      return true;
    }

    return false;
  }

  private _passwordsMismatch(): boolean {
    const password = this.newUser.get('password');
    const passwordConfirm = this.newUser.get('passwordConfirm');
    if (!password || !passwordConfirm) {
      return false;
    }
    return (
      !!password.value &&
      !!passwordConfirm.value &&
      password.value !== passwordConfirm.value
    );
  }

  private _syncFormFromUser(): void {
    if (!this.newUser) {
      return;
    }
    this.newUser.patchValue({
      firstName: this.user.firstName || '',
      lastName: this.user.lastName || '',
      email: this.user.email || '',
      phone: this.user.phone || '',
      companyName: this.user.companyName || '',
      password: this.user.password || '',
      passwordConfirm: this.user.confirmPassword || ''
    });
  }

  private _getValidationMessage(): string | null {
    if (!this.newUser) {
      return 'Please fill out the form correctly.';
    }

    if (this.user.userType !== 'Homeowner' && !this.user.companyName) {
      return 'Company name is required for your account type.';
    }

    const phoneControl = this.newUser.get('phone');
    if (phoneControl && phoneControl.errors && phoneControl.errors.invalidPhone) {
      return 'Phone number must contain at least 7 digits.';
    }

    const password = this.newUser.get('password');
    const passwordConfirm = this.newUser.get('passwordConfirm');
    if (this._passwordsMismatch()) {
      return 'Passwords do not match.';
    }

    if (this.newUser.invalid) {
      return 'Please fill out all required fields correctly.';
    }

    return null;
  }

  private _scrollToFirstInvalidField(): void {
    setTimeout(() => {
      const firstInvalid = document.querySelector('#register .field-error');
      if (firstInvalid && firstInvalid.scrollIntoView) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const footer = document.querySelector('.register-footer');
      if (footer && footer.scrollIntoView) {
        footer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 0);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/common/login-register/register/register.component.ts